import { ActionIcon, Box, Divider, Flex, Grid, Group, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { useUserData } from 'hooks/api/useUser';
import { t } from 'i18next';
import { useState } from 'react';
import { BsCheck, BsClock, BsPencil, BsTrash, BsX } from 'react-icons/bs';
import { useUserNameById } from 'services/comments';
import { formatDate } from 'utils/dates';
import { showFullName } from 'utils/helpers';

interface NoteProps {
  id?: string;
  text: string;
  updateTimestamp?: Date;
  author: string;
  editTooltipLabel: string;
  deleteTooltipLabel: string;
  handleDeleteLogging: (entryId: string) => void;
  handleUpdateLogging: (entryId: string, newNote: string) => void;
}

export const Note = ({
  id,
  text,
  updateTimestamp,
  author,
  editTooltipLabel,
  deleteTooltipLabel,
  handleDeleteLogging,
  handleUpdateLogging,
}: NoteProps) => {
  const userData = useUserData();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [notesText, setNotesText] = useState<string>(text);

  const user = useUserNameById([author]);

  const handleUpdateAction = async () => {
    if (!id) return;
    handleUpdateLogging(id, notesText);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNotesText(text);
  };

  const handleDeleteAction = (noteId?: string) => {
    if (!noteId) return;
    handleDeleteLogging(noteId);
  };

  if (isEditing) {
    return (
      <Grid align={'center'}>
        <Grid.Col span={11}>
          <Textarea
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            my={'lg'}
            autosize
            minRows={2}
            maxLength={250}
          />
        </Grid.Col>
        <Grid.Col span={1}>
          <Stack gap={'xs'}>
            <Tooltip label={`${t('confirm_label')}`}>
              <ActionIcon
                variant={'outline'}
                color={'var(--mantine-color-blue-5)'}
                disabled={!notesText}
                onClick={() => handleUpdateAction()}
              >
                <BsCheck size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={`${t('cancel_label')}`}>
              <ActionIcon variant={'outline'} color={'var(--mantine-color-red-5)'} onClick={() => handleCancelEdit()}>
                <BsX size={16} />
              </ActionIcon>
            </Tooltip>
          </Stack>
        </Grid.Col>
      </Grid>
    );
  }
  return (
    <Box>
      <Text>{text}</Text>
      <Flex justify={'space-between'}>
        <Group gap={'xs'}>
          <BsClock color={'gray'} size={14} />
          <Text size={'xs'} fw={500} c={'gray'}>
            {updateTimestamp && formatDate({ timestamp: updateTimestamp, formatWithTime: true })}
            {user.data && ` - ${showFullName(user.data?.[0])}`}
          </Text>
        </Group>
        {userData.data?.user.id === author && (
          <Group gap={'xs'}>
            <Tooltip label={editTooltipLabel}>
              <ActionIcon
                variant={'outline'}
                color={'var(--mantine-color-blue-5)'}
                size={'xs'}
                onClick={() => setIsEditing(true)}
              >
                <BsPencil size={12} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={deleteTooltipLabel}>
              <ActionIcon variant={'outline'} color={'red'} size={'xs'} onClick={() => handleDeleteAction(id)}>
                <BsTrash size={12} />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
      </Flex>
      <Divider my={'sm'} />
    </Box>
  );
};
